import { api } from './client'
import type { CalendarBookingDto, CourseMaterialDto, DayOfWeekName, SignatureType, TrainerSignatureDto, TrainerUnavailabilityDto } from './admin'

export type { CalendarBookingDto, CourseMaterialDto, DayOfWeekName, SignatureType, TrainerSignatureDto, TrainerUnavailabilityDto }

export type MyTrainerProfileDto = {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string | null
  qualifications: {
    courseId: string
    courseName: string
    courseCode: string | null
  }[]
  region: string
  timeZoneId: string
}

export type MyTrainerBookingDto = {
  bookingId: string
  courseName: string
  courseCode: string | null
  clientName: string | null
  bookingDate: string
  places: number
  status: string
  role: string
  venueName: string | null
  kind: 'Booking' | 'Session'
}

export type MyTrainerCourseDto = {
  courseId: string
  name: string
  code: string | null
  category: string | null
  description: string | null
  durationHours: number | null
  durationMinutes: number | null
  defaultDays: number
  durationLabel: string | null
  defaultExpiryMonths: number | null
}

export type AttendanceStatus = 'NotRecorded' | 'Present' | 'Absent'
export type AttendanceOutcome = 'Passed' | 'Failed'

export type RegisterAttendeeDto = {
  attendeeId: string
  staffMemberId: string
  firstName: string
  lastName: string
  email: string | null
  attendanceStatus: AttendanceStatus
  outcome: AttendanceOutcome | null
  isWalkIn: boolean
  photoUpdatedAt: string | null
  identityCheckedAt: string | null
  identityCheckedBy: string | null
}

export type CoTrainerDto = {
  firstName: string
  lastName: string
  role: string
}

export type BookingRegisterDto = {
  bookingId: string
  courseId: string
  courseName: string
  courseCode: string | null
  clientName: string
  clientContactName: string | null
  clientContactEmail: string | null
  clientPhone: string | null
  bookingDate: string
  places: number
  venueName: string | null
  venueAddressLine1: string | null
  venueCity: string | null
  venuePostcode: string | null
  venueNotes: string | null
  notes: string | null
  isCompleted: boolean
  isCancelled: boolean
  isRejected: boolean
  rejectionReason: string | null
  myRole: string
  coTrainers: CoTrainerDto[]
  attendees: RegisterAttendeeDto[]
  photosEnabled: boolean
  photoNotice: string | null
  requirePhotoCheck: boolean
  deliveryMethod: 'InPerson' | 'Online'
  meetingUrl: string | null
  meetingInstructions: string | null
}

export type StaffSearchResultDto = {
  staffMemberId: string
  firstName: string
  lastName: string
  email: string | null
}

export type SessionRegisterAttendeeDto = {
  attendeeId: string
  fullName: string
  companyName: string | null
  email: string
  attendanceStatus: AttendanceStatus
  outcome: AttendanceOutcome | null
  bookingAdminNotes: string | null
  photoUpdatedAt: string | null
  identityCheckedAt: string | null
  identityCheckedBy: string | null
}

export type SessionRegisterDto = {
  sessionId: string
  courseId: string
  courseName: string
  courseCode: string | null
  sessionDate: string
  totalPlaces: number
  bookedPlaces: number
  venueName: string | null
  venueAddressLine1: string | null
  venueCity: string | null
  venuePostcode: string | null
  venueNotes: string | null
  notes: string | null
  isCompleted: boolean
  isCancelled: boolean
  myRole: string
  coTrainers: CoTrainerDto[]
  attendees: SessionRegisterAttendeeDto[]
  photosEnabled: boolean
  photoNotice: string | null
  requirePhotoCheck: boolean
  deliveryMethod: 'InPerson' | 'Online'
  meetingUrl: string | null
  meetingInstructions: string | null
}

export const trainerApi = {
  getMyProfile: () => api.get<MyTrainerProfileDto>('/trainer-portal/me'),
  getMyBookings: () => api.get<MyTrainerBookingDto[]>('/trainer-portal/me/bookings'),
  getMyCourses: () => api.get<MyTrainerCourseDto[]>('/trainer-portal/me/courses'),
  getMyCalendar: (year: number, month: number) =>
    api.get<CalendarBookingDto[]>(`/trainer-portal/me/calendar?year=${year}&month=${month}`),
  getCalendarFeedToken: () => api.get<{ token: string | null }>('/trainer-portal/me/calendar-feed-token'),
  generateCalendarFeedToken: () => api.post<{ token: string }>('/trainer-portal/me/calendar-feed-token', {}),

  // Unavailability (self-service)
  getMyUnavailability: () => api.get<TrainerUnavailabilityDto[]>('/trainer-portal/me/unavailability'),
  addMyUnavailability: (data: { startDate: string; endDate: string; reason: string | null }) =>
    api.post<TrainerUnavailabilityDto>('/trainer-portal/me/unavailability', data),
  addMyRecurringUnavailability: (data: { daysOfWeek: DayOfWeekName[]; startDate: string; endDate: string | null; reason: string | null }) =>
    api.post<TrainerUnavailabilityDto[]>('/trainer-portal/me/unavailability/recurring', data),
  removeMyUnavailability: (id: string) => api.delete(`/trainer-portal/me/unavailability/${id}`),
  removeMyUnavailabilityGroup: (groupId: string) => api.delete(`/trainer-portal/me/unavailability/group/${groupId}`),
  getCourseMaterials: (courseId: string) =>
    api.get<CourseMaterialDto[]>(`/courses/${courseId}/materials`),
  getCourseMaterialDownloadUrl: (courseId: string, materialId: string) =>
    api.get<{ url: string }>(`/courses/${courseId}/materials/${materialId}/download-url`),

  getMySignature: () =>
    api.get<TrainerSignatureDto>('/trainer-portal/me/signature'),
  updateMySignature: (signatureType: SignatureType, signatureText: string | null) =>
    api.put('/trainer-portal/me/signature', { signatureType, signatureText }),
  uploadMySignatureImage: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<{ url: string }>('/trainer-portal/me/signature/image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Register
  getBookingRegister: (bookingId: string) =>
    api.get<BookingRegisterDto>(`/trainer-portal/bookings/${bookingId}/register`),
  searchClientStaff: (bookingId: string, q: string) =>
    api.get<StaffSearchResultDto[]>(`/trainer-portal/bookings/${bookingId}/staff-search`, { params: { q } }),
  updateAttendance: (bookingId: string, attendeeId: string, status: AttendanceStatus, outcome?: AttendanceOutcome | null) =>
    api.put(`/trainer-portal/bookings/${bookingId}/attendees/${attendeeId}/attendance`, { status, outcome }),
  addWalkIn: (bookingId: string, data: { existingStaffMemberId?: string; firstName?: string; lastName?: string; email?: string }) =>
    api.post<RegisterAttendeeDto>(`/trainer-portal/bookings/${bookingId}/attendees/walk-in`, data),
  getBookingAttendeePhoto: (bookingId: string, attendeeId: string) =>
    api.get<Blob>(`/trainer-portal/bookings/${bookingId}/attendees/${attendeeId}/photo`, { responseType: 'blob' }),
  setBookingAttendeePhoto: (bookingId: string, attendeeId: string, photo: Blob) => {
    const f = new FormData(); f.append('file', photo, 'photo.jpg')
    return api.post(`/trainer-portal/bookings/${bookingId}/attendees/${attendeeId}/photo`, f)
  },
  setBookingIdentityCheck: (bookingId: string, attendeeId: string, checked: boolean) =>
    api.put(`/trainer-portal/bookings/${bookingId}/attendees/${attendeeId}/identity-check`, { checked }),
  getSessionAttendeePhoto: (sessionId: string, attendeeId: string) =>
    api.get<Blob>(`/trainer-portal/sessions/${sessionId}/attendees/${attendeeId}/photo`, { responseType: 'blob' }),
  setSessionAttendeePhoto: (sessionId: string, attendeeId: string, photo: Blob) => {
    const f = new FormData(); f.append('file', photo, 'photo.jpg')
    return api.post(`/trainer-portal/sessions/${sessionId}/attendees/${attendeeId}/photo`, f)
  },
  setSessionIdentityCheck: (sessionId: string, attendeeId: string, checked: boolean) =>
    api.put(`/trainer-portal/sessions/${sessionId}/attendees/${attendeeId}/identity-check`, { checked }),
  removeWalkIn: (bookingId: string, attendeeId: string) =>
    api.delete(`/trainer-portal/bookings/${bookingId}/attendees/${attendeeId}`),
  completeRegister: (bookingId: string) =>
    api.post(`/trainer-portal/bookings/${bookingId}/complete`),
  rejectBooking: (bookingId: string, reason: string) =>
    api.post(`/trainer-portal/bookings/${bookingId}/reject`, { reason }),

  // Session register (open-enrolment sessions)
  getSessionRegister: (sessionId: string) =>
    api.get<SessionRegisterDto>(`/trainer-portal/sessions/${sessionId}/register`),
  updateSessionAttendance: (sessionId: string, attendeeId: string, status: AttendanceStatus, outcome?: AttendanceOutcome | null) =>
    api.put(`/trainer-portal/sessions/${sessionId}/attendees/${attendeeId}/attendance`, { status, outcome }),
  completeSession: (sessionId: string) =>
    api.post(`/trainer-portal/sessions/${sessionId}/complete`),
}
